import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { X, CheckCircle2, XCircle, Award } from "lucide-react";

const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

export async function printCertificate(courseId) {
  try {
    const { data: c } = await api.get(`/courses/${courseId}/certificate`);
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow pop-ups to print your certificate"); return; }
    const when = new Date(c.completed_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    w.document.write(`<!doctype html><html><head><title>Certificate — ${esc(c.course)}</title><style>
      body{font-family:Georgia,serif;margin:0;display:flex;align-items:center;justify-content:center;min-height:100vh;background:#fff}
      .c{border:10px double #1f7a6d;padding:60px 80px;max-width:760px;text-align:center}
      h1{font-size:40px;color:#1f7a6d;margin:0 0 8px} .n{font-size:32px;margin:24px 0;border-bottom:1px solid #ccc;padding-bottom:8px}
      .m{color:#555;font-size:15px;line-height:1.7} .co{font-size:24px;font-weight:bold;margin:12px 0}
    </style></head><body><div class="c"><img src="${window.location.origin}/logo.png" width="70" alt=""/><h1>Certificate of Completion</h1>
      <div class="m">This certifies that</div><div class="n">${esc(c.name)}</div><div class="m">has successfully completed</div>
      <div class="co">${esc(c.course)}</div><div class="m">with a score of ${esc(c.score)}% on ${esc(when)}${c.expires_on ? `<br/>Refresher due by ${esc(c.expires_on)}` : ""}</div>
      <p class="m" style="margin-top:36px">Brightside Befriending Services<br/><small>Certificate ID ${esc(c.certificate_id)}</small></p></div>
      <script>window.onload=()=>setTimeout(()=>window.print(),300)</script></body></html>`);
    w.document.close();
  } catch (e) { toast.error(apiError(e)); }
}

function Quiz({ course, onDone }) {
  const [ans, setAns] = useState(course.questions.map(() => null));
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (ans.some((a) => a === null)) { toast.error("Please answer every question"); return; }
    setBusy(true);
    try { const { data } = await api.post(`/courses/${course.id}/attempt`, { answers: ans }); setRes(data); if (data.passed) onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="course-quiz">
      {course.questions.map((q, i) => (
        <div key={i} className="rounded-lg border border-border p-4" data-testid={`quiz-question-${i}`}>
          <p className="mb-2 flex items-start gap-2 font-medium text-ink">
            {res && (res.results[i] ? <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-sage-dark" /> : <XCircle size={18} className="mt-0.5 shrink-0 text-coral-dark" />)}
            {i + 1}. {q.q}
          </p>
          <div className="space-y-1.5">
            {q.options.map((o, j) => (
              <label key={j} className={`flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${ans[i] === j ? "bg-brand-light text-ink" : "hover:bg-secondary"}`}>
                <input type="radio" name={`q${i}`} checked={ans[i] === j} disabled={!!res?.passed} onChange={() => { setAns(ans.map((a, k) => (k === i ? j : a))); setRes(null); }} data-testid={`quiz-option-${i}-${j}`} /> {o}
              </label>
            ))}
          </div>
        </div>
      ))}
      {res && (
        <div data-testid="quiz-result" className={`rounded-lg px-4 py-3 text-sm font-semibold ${res.passed ? "bg-sage-light text-sage-dark" : "bg-coral-light text-coral-dark"}`}>
          You scored {res.score}% (pass mark {res.pass_mark}%). {res.passed ? "Well done — you've passed!" : "Not quite. Review the content and try again."}
        </div>
      )}
      {res?.passed
        ? <button onClick={() => printCertificate(course.id)} data-testid="print-certificate-button" className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2.5 font-semibold text-white hover:opacity-90"><Award size={18} /> Print certificate</button>
        : <button onClick={submit} disabled={busy} data-testid="submit-quiz-button" className="rounded-lg bg-brand px-4 py-2.5 font-semibold text-white hover:bg-brand-dark disabled:opacity-60">{busy ? "Marking…" : "Submit answers"}</button>}
    </div>
  );
}

export function CourseViewer({ courseId, canTake, onClose }) {
  const [course, setCourse] = useState(null);
  const [step, setStep] = useState("read");
  const [passed, setPassed] = useState(false);
  useEffect(() => { api.get(`/courses/${courseId}`).then((r) => setCourse(r.data)).catch((e) => toast.error(apiError(e))); }, [courseId]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={() => onClose(passed)} />
      <div className="relative z-10 max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl" data-testid="course-viewer">
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="font-heading text-2xl font-bold text-ink">{course?.title || "Loading…"}</h2>
          <button onClick={() => onClose(passed)} data-testid="close-course-viewer"><X size={20} className="text-ink-muted" /></button>
        </div>
        {course && step === "read" && (
          <div className="space-y-5" data-testid="course-content">
            {course.summary && <p className="text-ink-soft">{course.summary}</p>}
            {(course.sections || []).map((s, i) => (
              <section key={i}><h3 className="mb-1 font-heading text-lg font-semibold text-brand">{s.heading}</h3><p className="whitespace-pre-line leading-relaxed text-ink">{s.body}</p></section>
            ))}
            {canTake && course.questions?.length > 0 && <button onClick={() => setStep("quiz")} data-testid="start-quiz-button" className="rounded-lg bg-brand px-4 py-2.5 font-semibold text-white hover:bg-brand-dark">I've read this — take the quiz</button>}
          </div>
        )}
        {course && step === "quiz" && <Quiz course={course} onDone={() => setPassed(true)} />}
      </div>
    </div>
  );
}
